import { resolveDeckDomain, resolveDeckTopic } from '../utils/deckDisplay';

export function getUpcomingExams(exams) {
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const upcoming = [];
  exams.forEach(e => {
    if (e.completed_at && !e.revived) {
      return;
    } else if (e.revived) {
      upcoming.push(e);
    } else {
      const dateStr = typeof e.exam_date === 'string' ? e.exam_date.split('T')[0] : e.exam_date;
      const parts = typeof dateStr === 'string' ? dateStr.split('-').map(Number) : null;
      const d = parts && parts.length === 3
        ? new Date(parts[0], parts[1] - 1, parts[2])
        : new Date(e.exam_date);
      d.setHours(0, 0, 0, 0);
      if (d >= todayStart) upcoming.push(e);
    }
  });
  upcoming.sort((a, b) => new Date(a.exam_date) - new Date(b.exam_date));
  return upcoming;
}

export function buildHierarchy(decks, deckToExamMap) {
  const result = {};
  const sortedDecks = [...decks].sort((a, b) =>
    new Date(a.uploaded_at) - new Date(b.uploaded_at)
  );

  sortedDecks.forEach(deck => {
    if (deck.source === 'naplex_learning' || deck.source === 'ptcb_learning' || deck.source === 'mpje_learning' || deck.source === 'naplex' || deck.source === 'ptcb' || deck.source === 'mpje') return;

    const isProcessing = deck.parsing_status === 'parsing' || deck.parsing_status === 'pending' || deck.parsing_status === 'uploading';
    const processingLabel = deck.filename
      ? deck.filename.replace(/\.(pptx?|pdf)$/i, '')
      : 'Processing...';
    const domain = isProcessing ? 'Processing Upload' : resolveDeckDomain(deck);
    const topic = isProcessing
      ? processingLabel
      : resolveDeckTopic(deck, { processingLabel });
    const domainColor = deck.domain_color || '#6366f1';
    const topicColor = deck.topic_color || '#8b5cf6';

    const deckExams = isProcessing ? [] : (deckToExamMap[deck.id] || []);
    const examKey = deckExams.length > 0 ? deckExams[0].id : 'unlinked';
    const examData = deckExams.length > 0
      ? deckExams[0]
      : { id: 'unlinked', title: 'Not Linked to Exam' };

    if (!result[domain]) {
      result[domain] = { exams: {}, color: domainColor, topicCount: 0 };
    }

    if (!result[domain].exams[examKey]) {
      result[domain].exams[examKey] = {
        id: examData.id, title: examData.title, exam_date: examData.exam_date,
        completed_at: examData.completed_at,
        topics: {}, topicCount: 0
      };
    }

    if (!result[domain].exams[examKey].topics[topic]) {
      result[domain].exams[examKey].topics[topic] = {
        key: `${domain}::${examKey}::${topic}`,
        decks: [], objectives: [], color: topicColor, slideCount: 0, uploadedAt: null,
      };
      result[domain].exams[examKey].topicCount++;
      result[domain].topicCount++;
    }

    if (deck.uploaded_at) {
      const deckDate = new Date(deck.uploaded_at);
      const currentDate = result[domain].exams[examKey].topics[topic].uploadedAt;
      if (!currentDate || deckDate > new Date(currentDate)) {
        result[domain].exams[examKey].topics[topic].uploadedAt = deck.uploaded_at;
      }
    }

    result[domain].exams[examKey].topics[topic].decks.push(deck);
    result[domain].exams[examKey].topics[topic].slideCount += deck.slide_count || 0;
  });

  return result;
}

export function buildSidebarHierarchy(hierarchy, activeExamId) {
  const examDomains = new Set();
  if (activeExamId) {
    Object.entries(hierarchy).forEach(([domain, domainData]) => {
      if (domainData.exams && domainData.exams[activeExamId]) {
        examDomains.add(domain);
      }
    });
  }

  const filtered = {};
  Object.entries(hierarchy).forEach(([domain, domainData]) => {
    if (activeExamId && !examDomains.has(domain)) return;

    const filteredExams = {};
    let topicCount = 0;
    Object.entries(domainData.exams || {}).forEach(([examKey, examData]) => {
      if (examKey === 'unlinked' || examKey === activeExamId) {
        filteredExams[examKey] = examData;
        topicCount += examData.topicCount || 0;
      }
    });
    if (Object.keys(filteredExams).length > 0 && topicCount > 0) {
      filtered[domain] = { ...domainData, exams: filteredExams, topicCount };
    }
  });
  if (hierarchy['Processing Upload'] && !filtered['Processing Upload']) {
    filtered['Processing Upload'] = hierarchy['Processing Upload'];
  }
  return filtered;
}

export function sortDomains(hierarchy) {
  return [...Object.keys(hierarchy)].sort((a, b) => {
    const getNewest = (d) => {
      let newest = null;
      Object.values(d.exams || {}).forEach(exam => {
        Object.values(exam.topics || {}).forEach(t => {
          if (t.uploadedAt && (!newest || new Date(t.uploadedAt) > new Date(newest))) {
            newest = t.uploadedAt;
          }
        });
      });
      return newest;
    };
    const dA = getNewest(hierarchy[a]);
    const dB = getNewest(hierarchy[b]);
    if (dA && dB) return new Date(dB) - new Date(dA);
    if (dA) return -1;
    if (dB) return 1;
    return a.localeCompare(b);
  });
}
